import { useCallback, useEffect, useState, type JSX } from 'react';
import { useParams } from '@tanstack/react-router';
import type { PortalBankInfoRequest, PortalBankView, PortalConsentDocument, PortalForm, PortalFieldKey } from '@procuai/contracts';
import { SupplierPortal, type SupplierPortalAdapter, type PortalBankInfoView, type PortalConsentDocView, type PortalFormView } from '@procuai/ui';
import { api, ApiError } from '../api.js';

/**
 * The public supplier portal page (BP-02 §2.4). It lives OUTSIDE the authenticated app shell: the
 * supplier is not a ProcuAI user, opens it from an emailed token link, and sees no navigation. It
 * reads GET /portal/:token, autosaves edits via PUT /portal/:token/fields, and finalises with
 * POST /portal/:token/submit. Bank details (D-141) are read via GET /portal/:token/bank and saved via
 * POST /portal/:token/bank-info (those endpoints require consent, so they 403 until accepted). An invalid
 * or expired token (404) renders the friendly error state in the SupplierPortal component.
 */
function toView(f: PortalForm): PortalFormView {
  return {
    status: f.status,
    orgName: f.orgName,
    supplierName: f.supplierName,
    contactName: f.contactName,
    expiresAt: f.expiresAt,
    consentAccepted: f.consentAccepted,
    consentVersion: f.consentVersion,
    consentTitle: f.consentTitle,
    consentBodyMd: f.consentBodyMd,
    fields: f.fields.map((x) => ({ key: x.key, label: x.label, value: x.value, fromRecord: x.fromRecord })),
    contractName: f.contractName ?? null,
    contractTokens: (f.contractTokens ?? []).map((t) => ({ key: t.key, label: t.label, value: t.value, fromRecord: false })),
  };
}

function toBankView(b: PortalBankView): PortalBankInfoView {
  return { iban: b.iban, bic: b.bic, bankName: b.bankName, accountHolder: b.accountHolder, financeEmail: b.financeEmail, validationStatus: b.validationStatus };
}

export function Portal(): JSX.Element {
  const { token } = useParams({ from: '/portal/$token' });
  const [form, setForm] = useState<PortalFormView | null>(null);
  const [bank, setBank] = useState<PortalBankInfoView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    let live = true;
    setLoading(true);
    api<PortalForm>(`/portal/${token}`)
      .then((f) => { if (!live) return; setForm(toView(f)); if (f.status === 'submitted' || f.status === 'accepted') setSubmitted(true); })
      .catch((e: unknown) => { if (!live) return; setError(e instanceof ApiError ? e.message : 'This link is invalid or has expired.'); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [token]);

  // Bank section is only reachable past the consent gate (the endpoint 403s before consent), so load it
  // once consent is accepted.
  const loadBank = useCallback(async () => {
    try { setBank(toBankView(await api<PortalBankView>(`/portal/${token}/bank`))); } catch { /* before consent: ignored */ }
  }, [token]);

  useEffect(() => { if (form?.consentAccepted) void loadBank(); }, [form?.consentAccepted, loadBank]);

  const saveField = useCallback(async (key: string, value: string) => {
    await api<void>(`/portal/${token}/fields`, { method: 'PUT', body: JSON.stringify({ fields: [{ key: key as PortalFieldKey, value }] }) });
  }, [token]);

  const saveToken = useCallback(async (name: string, value: string) => {
    await api<void>(`/portal/${token}/fields`, { method: 'PUT', body: JSON.stringify({ tokens: [{ name, value }] }) });
  }, [token]);

  const submit = useCallback(async () => {
    await api<{ status: string; submitted: number }>(`/portal/${token}/submit`, { method: 'POST' });
    setSubmitted(true);
  }, [token]);

  const acceptConsent = useCallback(async () => {
    await api<void>(`/portal/${token}/consent`, { method: 'POST', body: JSON.stringify({ version: form?.consentVersion ?? '' }) });
    const f = await api<PortalForm>(`/portal/${token}`);
    setForm(toView(f));
    void loadBank();
  }, [token, form?.consentVersion, loadBank]);

  const requestErasure = useCallback(async () => {
    await api<void>(`/portal/${token}/erasure-request`, { method: 'POST' });
  }, [token]);

  const setBankInfo = useCallback(async (input: PortalBankInfoRequest) => {
    setBank(toBankView(await api<PortalBankView>(`/portal/${token}/bank-info`, { method: 'POST', body: JSON.stringify(input) })));
  }, [token]);

  // "What you accepted" (BP-22): the exact stored notice this invite accepted.
  const loadConsentDocument = useCallback(async (): Promise<PortalConsentDocView> => {
    const d = await api<PortalConsentDocument>(`/portal/${token}/consent-document`);
    return {
      acceptedVersion: d.acceptedVersion,
      acceptedAt: d.acceptedAt,
      acceptedTitle: d.accepted?.title ?? null,
      acceptedBodyMd: d.accepted?.bodyMd ?? null,
    };
  }, [token]);

  const adapter: SupplierPortalAdapter = { form, loading, error, saveField, saveToken, submit, acceptConsent, requestErasure, bank, setBankInfo, submitted, loadConsentDocument };
  return <SupplierPortal adapter={adapter} />;
}
